/**
 * CreateProductModal - Reuses ProductForm to create a product from within other flows (quotes/invoices)
 */
import React, { useMemo, useState } from "react";
import { Dialog, DialogTitle, DialogContent, Box, Button } from "@mui/material";
import { Form } from "antd";
import { createProduct } from "../services/productService";
import ProductForm from "./ProductForm";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onCreated - (product) => void
 * @param {Function} [props.onUseAsOneOff] - () => void
 * @param {Object} [props.initialValues]
 * @param {string} [props.title]
 */
const CreateProductModal = ({
  open,
  onClose,
  onCreated,
  onUseAsOneOff,
  initialValues = {},
  title = "Create Product",
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const normalizedInitialValues = useMemo(() => {
    const name = initialValues?.name?.trim?.() ? initialValues.name.trim() : "";
    const description = initialValues?.description?.trim?.()
      ? initialValues.description.trim()
      : name;

    return {
      category: "services",
      active: true,
      ...initialValues,
      ...(name ? { name } : {}),
      ...(description ? { description } : {}),
    };
  }, [initialValues]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const result = await createProduct(values);
      if (!result.success) {
        logError("Failed to create product from modal", {
          error: result.error,
          values,
        });
        return;
      }

      const created = result.data;
      logInfo("Product created from modal", { productId: created?.id });
      if (onCreated) onCreated(created);
      onClose();
    } catch (err) {
      logError("Failed to create product from modal", {
        error: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {onUseAsOneOff && (
            <Button
              variant="outlined"
              onClick={() => {
                if (loading) return;
                onUseAsOneOff();
                onClose();
              }}
            >
              Use as one-off item (don&apos;t save to Products)
            </Button>
          )}

          <ProductForm
            form={form}
            onFinish={handleSubmit}
            onCancel={onClose}
            loading={loading}
            initialValues={normalizedInitialValues}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProductModal;

